import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHyperlocalImage, useCurrentHyperlocal } from '../utils/hyperlocal';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  description: 'Falls back to the property featured image when this hyperlocal item has no featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
  },
};

// Escape user-supplied plain text before injecting it into a richtext default.
// Hyperlocal h1_title / h2_subtitle are stored as plain strings; wrapping them
// in <p>…</p> keeps the same DOM shape RichTextContent expects, but we must
// neutralize any HTML special characters so a user-typed `<` doesn't poison
// the markup.
function toRichTextDefault(text: string | undefined | null, fallback: string): string {
  if (!text) return fallback;
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

export function HyperlocalHero() {
  const hl = useCurrentHyperlocal();
  const bgImage = useSlotGroup('hyperlocalhero.bg_image', bgImageDef);

  // Image priority: this item's featured_image (unique per hyperlocal page)
  // > slot-group value / property_featured_image (shared fallback) > solid
  // placeholder. pickHyperlocalImage() drops generator sentinels.
  const displayImage = pickHyperlocalImage(hl?.featured_image) || pickHyperlocalImage(bgImage.image) || '';

  const titleDefault = toRichTextDefault(hl?.h1_title, '<p>Hyperlocal Page Title</p>');
  const subtitleDefault = toRichTextDefault(hl?.h2_subtitle, '<p>Explore apartments in your area</p>');

  return (
    <section className="relative w-full h-[calc(70vh-50px)] min-h-[400px] max-h-[650px] overflow-hidden">
      <EditableSlotGroup groupPath="hyperlocalhero.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt=""
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'hlKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* The container is absolutely filled and padded down by the fixed
          header height (+ offer banner) so the title/subtitle read as centered
          within the VISIBLE region below the header rather than across the full
          section — where justify-center alone would push text up behind the
          overlaid header and appear top-skewed. Same compensation as HeroImageBG. */}
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-white px-6 sm:px-8"
        style={{
          paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)',
          transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <RichTextContent
          path="hyperlocalhero.title"
          label="Title"
          defaultValue={titleDefault}
          as="h1"
          className="hl-entrance hl-entrance-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />
        <RichTextContent
          path="hyperlocalhero.subtitle"
          label="Subtitle"
          defaultValue={subtitleDefault}
          as="h2"
          className="hl-entrance hl-entrance-2 font-body text-lg sm:text-xl lg:text-2xl max-w-3xl leading-relaxed opacity-90 drop-shadow-md font-normal"
        />
      </div>

      <style>{`
        @keyframes hlKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes hlEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hl-entrance {
          animation: hlEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hl-entrance-1 { animation-delay: 0.2s; }
        .hl-entrance-2 { animation-delay: 0.45s; }
        @media (prefers-reduced-motion: reduce) {
          .hl-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalHero',
  displayName: 'Hyperlocal Hero',
  tokens: {
    'hyperlocalhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Hyperlocal Page Title</p>',
    },
    'hyperlocalhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore apartments in your area</p>',
    },
  },
  slotGroups: {
    'hyperlocalhero.bg_image': bgImageDef,
  },
};

(HyperlocalHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HyperlocalHero;
